import React, { MouseEventHandler, ReactNode, useEffect, useState } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Box, Typography } from '@material-ui/core';

import css from './programSectionDetailed.module.scss';
import Dialog from '../../../components/dialog/Dialog';
import { SectionChip, TileItem } from '../../../components/elements/Elements';
import { isCoffeeBreak, SectionEvent, SpeakersRow } from '../components';
import { timeRangeFormat } from '../../../hooks/moment';
import { ClassName, Elem } from '../../../components/types';
import { DownloadIcon } from '../../../components/Icons';
import { FileAttachment, Moderator, ProgramSpeech } from '../../../services/models';
import Button from '../../../components/buttons/Buttons';


export default function ProgramSectionDetailed({ open, onClose, section }: {
  open: boolean, onClose?: () => void,
  section?: SectionEvent,
}) {
  const { t } = useTranslation();
  const [item, setItem] = useState(section);

  useEffect(() => {
    if (section) setItem(section);
  }, [section]);

  let children = null;
  if (item) {
    children = (
      <Box display="flex" flexDirection="column" alignItems="flex-start">
        {item.name && <SectionChip>{item.name}</SectionChip>}
        <Box className={css.timeBlock} display="flex" alignItems="center">
          {/*<Typography>{item.date}</Typography>*/}
          <Typography className={css.time} variant="inherit">{timeRangeFormat(item.start, item.end)}</Typography>
          <Typography className={css.location} variant="inherit">{item.location}</Typography>
        </Box>
        <Typography className={css.title} variant="h3" color="secondary">{item.subject}</Typography>
        {Boolean(item.speakers?.length) && (
          <Block className={css.speakers} title={t('program_detailed.speakers')}>
            {item.speakers?.map(i => (
              <SpeakersRow className={css.speaker} key={i.id} speakers={[i]} clickable showOrganization />))}
          </Block>
        )}
        {Boolean(item.moderators?.length) && (
          <Block className={css.speakers} title={t('program_detailed.moderators')}>
            {item.moderators?.map(i => (
              <SpeakersRow className={css.speaker} key={i.id} speakers={[i]} clickable showOrganization />))}
          </Block>
        )}
        {item.description && (
          <Block className={css.descriptionBlock} title={t('program_detailed.description')}>
            <Typography className={css.description} component="div"
                        dangerouslySetInnerHTML={{ __html: item.description }} />
          </Block>
        )}
        {Boolean(item.files?.length) && (
          <Box className={css.files} display="flex" flexDirection="column">
            {item.files?.map(i => (<FileAttachmentCard key={i.url} className={css.file} file={i} />))}
          </Box>
        )}
        {Boolean(item.speeches?.length) && (
          <Box className={css.speeches} display="flex" flexDirection="column">
            {item.speeches?.map(i => (<ProgramSpeechCard key={i.id} item={i} />))}
          </Box>
        )}
      </Box>
    );
  }
  return (
    <Dialog className={css.ProgramSectionDetailed} open={open} onClose={onClose}>
      {children}
    </Dialog>
  );
}

export function Block({ className, title, children, onClick }: Elem & {
  title?: ReactNode, onClick?: MouseEventHandler<HTMLDivElement>
}) {
  return (
    <Box className={classnames(css.BlockRow, className)} display="flex" flexDirection="column" onClick={onClick}>
      <Typography className={css.grey} variant="inherit">{title}</Typography>
      {children}
    </Box>
  );
}

function FileAttachmentCard({ className, file }: ClassName & { file: FileAttachment }) {
  return (
    <Button className={className} href={file.url} download={file.title}
            startIcon={<DownloadIcon />}>
      <Typography>{file.title}</Typography>
    </Button>
  );
}

function ProgramSpeechCard({ className, item }: ClassName & { item: ProgramSpeech }) {
  const time = timeRangeFormat(item.start_at, item.finish_at);
  return (
    <Box
      className={classnames(css.ProgramSpeechCard, className, { [css.coffeeBreak]: isCoffeeBreak(item) })}
      display="flex" flexDirection="column"
    >
      <Typography className={css.ProgramSpeechCard__time} variant="inherit">{time}</Typography>
      <Typography className={css.ProgramSpeechCard__title} color="primary">{item.subject}</Typography>
      {/*<SpeakersRow className={css.ProgramSpeechCard__speakers} speakers={item.speakers} clickable showOrganization />*/}
      {Boolean(item.speakers?.length) && (
        <div className={css.ProgramSpeechCard__speakers}>
          {item.speakers?.map(i => (
            <SpeakersRow className={css.speaker} key={i.id} speakers={[i]} clickable showOrganization />))}
        </div>
      )}
    </Box>
  );
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function ModeratorCard({ className, moderator }: ClassName & { moderator: Moderator, }) {
  return (
    <TileItem
      className={classnames(className,)}
      title={moderator.full_name}
      subtitle={moderator?.organization}
    />
  );
}
